import type { LedgerReason, UsageSource } from "@/api/billing";

const MC = 1000;

/** Credits with up to 2 decimals for small amounts, none for large ones. */
export function formatCredits(millicredits: number) {
	const value = millicredits / MC;
	const abs = Math.abs(value);
	return value.toLocaleString(undefined, {
		maximumFractionDigits: abs >= 100 ? 0 : abs >= 1 ? 1 : 2,
	});
}

export const formatSignedCredits = (millicredits: number) =>
	`${millicredits > 0 ? "+" : millicredits < 0 ? "−" : ""}${formatCredits(Math.abs(millicredits))}`;

export const formatPrice = (cents: number) =>
	(cents / 100).toLocaleString(undefined, {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
	});

/** Provider cost: sub-cent precision so a $0.0021 caption doesn't read as $0.00. */
export function formatUsd(value: number) {
	if (value === 0) return "$0";
	if (Math.abs(value) < 0.01) return `$${value.toFixed(4)}`;
	return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export const formatLimit = (limit: number | null) =>
	limit == null ? "Unlimited" : limit.toLocaleString();

export const SOURCE_LABELS: Record<UsageSource, string> = {
	openrouter: "AI generations",
	brightdata: "Competitor scrapes",
	zernio: "Publishing",
	storage: "Storage",
	other: "Other",
};

export const REASON_LABELS: Record<LedgerReason, string> = {
	plan_grant: "Plan credits",
	pack_purchase: "Credit pack",
	usage: "Usage",
	refund: "Refund",
	adjustment: "Adjustment",
	expiration: "Expired",
	promo: "Promo",
};

const FEATURE_LABELS: Record<string, string> = {
	"assistant.chat": "Assistant chat",
	"competitor.scrape": "Competitor scrape",
	"social.publish.x": "Publish to X",
	"storage.overage": "Storage above 5 GB",
	"insights.generate": "Insights",
};

export function featureLabel(feature: string) {
	if (FEATURE_LABELS[feature]) return FEATURE_LABELS[feature];
	const parts = feature.split(".").map((part) => part.replace(/[_-]/g, " "));
	return parts
		.map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
		.join(" · ");
}
